// Compte de l'organisation : la clé d'IA.
//
// Une organisation peut brancher son propre compte Anthropic. Ses générations
// de texte passent alors par Claude, à ses frais. La clé se saisit une fois :
// elle est chiffrée par le serveur et n'est plus jamais réaffichée — seuls ses
// quatre derniers caractères le sont, pour la reconnaître.
//
// Seuls les administrateurs la gèrent (le serveur le garantit) ; les autres
// membres voient seulement si une clé est en place.
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiRequestError } from '../api/client';
import { Button, ErrorBanner, Eyebrow, Field, Spinner } from '../components/ui';
import { Link } from '../router';
import { VisioAccountsSection } from '../invitations/VisioAccountsSection';
import { DeviceSettingPicker } from '../components/DeviceSetting';
import { DemoUsageSection } from '../demo/DemoUsage';
import './account.css';

interface AiKey {
  present: boolean;
  provider: string | null;
  display: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  canManage: boolean;
}

const KEY = ['organization', 'ai-key'] as const;
const PATH = '/api/organization/ai-key';

const errorOf = (cause: unknown) => (cause instanceof ApiRequestError ? cause.message : 'Une erreur inattendue est survenue.');
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

function AiKeySection() {
  const qc = useQueryClient();
  const key = useQuery({ queryKey: KEY, queryFn: () => api.get<{ aiKey: AiKey }>(PATH).then((b) => b.aiKey) });
  const store = (data: { aiKey: AiKey }) => qc.setQueryData(KEY, data.aiKey);
  const save = useMutation({ mutationFn: (value: string) => api.put<{ aiKey: AiKey }>(PATH, { value }), onSuccess: store });
  const remove = useMutation({ mutationFn: () => api.del<{ aiKey: AiKey }>(PATH), onSuccess: store });
  const check = useMutation({
    mutationFn: () => api.post<{ result: { ok: boolean; message: string } }>(`${PATH}/test`).then((b) => b.result),
  });
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [confirming, setConfirming] = useState(false);

  if (key.isPending) return <Spinner label="Chargement…" />;
  if (key.isError || !key.data) return <ErrorBanner>{errorOf(key.error)}</ErrorBanner>;
  const k = key.data;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    check.reset();
    save.mutate(value, { onSuccess: () => { setEditing(false); setValue(''); } });
  };
  const failure = save.error ?? remove.error ?? check.error;

  return (
    <section className="zy-card account-section" aria-labelledby="ai-key-title">
      <div className="account-head">
        <div>
          <h2 className="t-h3" id="ai-key-title">Clé d’IA de l’organisation</h2>
          <p className="account-lede">
            Facultatif. Avec votre propre clé Anthropic, l’extraction de verbatims, le copilote, les synthèses et les
            propositions utilisent Claude, sur votre compte Anthropic et à vos frais.
          </p>
        </div>
        <span className={k.present ? 'account-pill is-on' : 'account-pill'}>
          {k.present ? (k.display ? `Clé ${k.display}` : 'Clé en place') : 'Aucune clé'}
        </span>
      </div>

      {k.present && k.updatedAt ? (
        <p className="account-meta">
          Enregistrée le {fmtDate(k.updatedAt)}
          {k.updatedBy ? ` par ${k.updatedBy}` : ''}.
        </p>
      ) : null}

      <ul className="account-notes">
        <li>
          Avec votre clé, les extraits envoyés à l’IA sont traités par Anthropic, qui peut les traiter hors de l’Union
          européenne, selon les conditions de votre compte Anthropic. Sans clé, l’IA de Zygosight reste hébergée
          dans l’UE.
        </li>
        <li>La transcription des enregistrements et la recherche par le sens restent assurées par Zygosight.</li>
        <li>La clé est chiffrée et ne peut plus être relue, ni par vous ni par nous. Vous pouvez la retirer à tout moment.</li>
      </ul>

      {!k.canManage ? (
        <p className="account-meta">Seuls les administrateurs de l’organisation gèrent cette clé.</p>
      ) : editing ? (
        <form className="account-form" onSubmit={submit}>
          <Field
            label="Clé d’API Anthropic"
            type="password"
            autoComplete="off"
            placeholder="sk-ant-…"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            hint="À créer dans la console Anthropic, rubrique « API keys »."
            required
          />
          <div className="account-actions">
            <Button type="submit" variant="accent" loading={save.isPending}>Enregistrer</Button>
            <Button type="button" variant="ghost" onClick={() => { setEditing(false); setValue(''); save.reset(); }}>
              Annuler
            </Button>
          </div>
        </form>
      ) : confirming ? (
        <div className="account-actions">
          <span className="account-meta">Retirer la clé ? L’organisation repassera sur l’IA de Zygosight.</span>
          <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate(undefined, { onSettled: () => setConfirming(false) })}>
            Retirer
          </Button>
          <Button variant="ghost" onClick={() => setConfirming(false)}>Annuler</Button>
        </div>
      ) : (
        <div className="account-actions">
          <Button variant={k.present ? 'ghost' : 'accent'} onClick={() => setEditing(true)}>
            {k.present ? 'Remplacer la clé' : 'Ajouter ma clé'}
          </Button>
          {k.present ? (
            <>
              <Button variant="ghost" loading={check.isPending} onClick={() => check.mutate()}>Tester la clé</Button>
              <Button variant="ghost" onClick={() => { check.reset(); setConfirming(true); }}>Retirer</Button>
            </>
          ) : null}
        </div>
      )}

      {check.data ? <p className={check.data.ok ? 'account-result is-ok' : 'account-result'}>{check.data.message}</p> : null}
      {failure ? <ErrorBanner>{errorOf(failure)}</ErrorBanner> : null}
    </section>
  );
}

export function AccountPage() {
  return (
    <div className="app-main account">
      <div className="zy-page-head__texte">
        <Eyebrow>
          <Link to="/">Portefeuille</Link> · Compte
        </Eyebrow>
        <h1 className="t-h1">Compte de l’organisation</h1>
        <p className="zy-intro">Les réglages de votre organisation, et vos connexions personnelles.</p>
      </div>
      <DemoUsageSection />
      <AiKeySection />
      <VisioAccountsSection />
      {/* Réglage propre à CE navigateur : utile quand la détection se trompe
          (tablette avec clavier, par exemple). */}
      <section className="zy-card account-section" aria-label="Cet appareil">
        <DeviceSettingPicker />
      </section>
    </div>
  );
}
